import React, {
  useDeferredValue,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Badge,
  Button,
  Dropdown,
  Select,
  Spin,
  Typography,
} from "antd";
import { useAppMessage } from "../../hooks/useAppMessage";
import {
  ExportOutlined,
  SearchOutlined,
  EllipsisOutlined,
  PushpinOutlined,
  GroupOutlined,
} from "@ant-design/icons";
import { Helmet } from "react-helmet";
import { formatInTimeZone } from "date-fns-tz";
import HistoryTable from "../../Components/HistoryTable/HistoryTable";
import { HistoryFilter } from "../../Components/modals/HistoryFilter";
import { historyAPI } from "../../API/HistoryAPI";
import HeaderTab from "../../Components/headerTab/HeaderTab";
import { HistorySelectedColumns } from "../../Components/modals/HisorySelectedColumns";
import { DEFUALT_COLUMNS, isSortableColumnKey, getNextSortState, HISTORY_GRID_DEFAULT_WIDTHS, GRID_PREFERENCE_MESSAGES } from "./HistoryUtils";
import { HistoryFreezeColumns } from "../../Components/modals/HistoryFreezeColumns";
import BottomTab from "../../Components/BottomTab/BottomTab";
import { useFlightModel } from "../../contexts/FlightModelContext";
import {
  extractPreferences,
  toWidthMap,
  toFrozenMap,
  fromApiColumnLayoutKey,
  toApiColumnLayoutKey,
} from "../../Components/DndTable/gridPreferencesUtils";
const { Text } = Typography;

export const History = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [messageApi, contextHolder] = useAppMessage();
  const messageToast = useCallback(
    (type, message, key) => {
      messageApi.open({
        type: type,
        content: message,
        duration: 6,
        // A stable key makes repeat calls replace the existing toast instead
        // of stacking identical messages.
        ...(key ? { key } : {}),
      });
    },
    [messageApi],
  );
  const [HistoryData, setHistoryData] = useState([]);
  // Grid preferences are stored server side only. These are the defaults rendered
  // until loadGridPreferences() hydrates them from the API — not a local cache.
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [openselectColumns, setOpenSelectColumns] = useState(false);
  const [openHistoryFilter, setOpenHistoryFilter] = useState(false);
  const [openFreezeColumns, setOpenFreezeColumns] = useState(false);
  const [frozenColumns, setFrozenColumns] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [sortBy, setSortBy] = useState("wpm_score");
  const [sortDir, setSortDir] = useState("desc");

  const hasLoadedPreferencesRef = React.useRef(false);
  const saveTimeoutRef = React.useRef(null);
  // Holds the user action ("freeze" | "reorder" | "resize") awaiting a save, so the
  // toast can name it. Stays null for saves with nothing to report, e.g. sorting.
  const pendingActionRef = React.useRef(null);

  const markPreferenceAction = useCallback((action) => {
    pendingActionRef.current = action;
  }, []);

  // Reports whether the server layout is now known. The caller gates saving on it:
  // with no local cache to fall back to, writing back after a failed load would
  // push the default columns over whatever the user actually has stored.
  const loadGridPreferences = useCallback(async () => {
    try {
      const response = await historyAPI.getHistoryGridPreferences();
      if (!response || response.status >= 400) {
        setSelectedColumns(DEFUALT_COLUMNS);
        return false;
      }

      const preferences = extractPreferences(response);

      if (preferences.sort_by) {
        setSortBy(preferences.sort_by);
      }
      if (preferences.sort_dir !== undefined && preferences.sort_dir !== null) {
        setSortDir(preferences.sort_dir);
      }

      const layout = preferences.column_layout || {};
      if (Object.keys(layout).length > 0) {
        const widths = toWidthMap(layout);
        setColumnWidths(widths);

        const frozen = toFrozenMap(layout);
        const activeFrozen = Object.keys(frozen).filter(k => frozen[k]);
        setFrozenColumns(activeFrozen);

        const sortedCols = Object.entries(layout)
          .map(([columnKey, val]) => ({
            key: columnKey,
            order: val?.order ?? 999,
          }))
          .sort((a, b) => a.order - b.order)
          .map(item => item.key);

        if (sortedCols.length > 0) {
          setSelectedColumns(sortedCols);
        } else {
          setSelectedColumns(DEFUALT_COLUMNS);
        }
      } else {
        setSelectedColumns(DEFUALT_COLUMNS);
      }

      // An empty layout is a valid answer: the user has no saved preferences yet,
      // so the defaults stand and their first change creates the record.
      return true;
    } catch (e) {
      console.error("Failed to load history grid preferences from API:", e);
      setSelectedColumns(DEFUALT_COLUMNS);
      return false;
    }
  }, []);

  const persistPreferences = useCallback(
    async (currentCols, currentFrozen, currentWidths, currentSortBy, currentSortDir) => {
      if (!hasLoadedPreferencesRef.current) {
        return;
      }

      const messages = GRID_PREFERENCE_MESSAGES[pendingActionRef.current];
      pendingActionRef.current = null;

      const columnLayout = {};
      currentCols.forEach((columnKey, index) => {
        const isFrozen = currentFrozen.includes(columnKey);
        const resolvedWidth = currentWidths[columnKey] || HISTORY_GRID_DEFAULT_WIDTHS[columnKey] || 120;
        
        columnLayout[columnKey] = {
          width: Math.round(resolvedWidth),
          frozen: isFrozen,
          order: index,
        };
      });

      const payload = {
        sort_by: currentSortBy,
        sort_dir: currentSortDir,
        column_layout: columnLayout,
      };

      try {
        const response = await historyAPI.updateHistoryGridPreferences(payload);
        if (!messages) return;

        if (response?.data?.status === true || response?.status === true) {
          messageToast("success", messages.success);
        } else {
          messageToast("error", response?.data?.message || messages.error);
        }
      } catch (e) {
        console.error("Failed to save history grid preferences:", e);
        if (messages) {
          messageToast("error", messages.error);
        }
      }
    },
    [messageToast],
  );

  const schedulePersist = useCallback(
    (currentCols, currentFrozen, currentWidths, currentSortBy, currentSortDir) => {
      if (!hasLoadedPreferencesRef.current) return;

      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = window.setTimeout(() => {
        persistPreferences(currentCols, currentFrozen, currentWidths, currentSortBy, currentSortDir);
      }, 500);
    },
    [persistPreferences],
  );

  useEffect(() => {
    if (hasLoadedPreferencesRef.current) {
      schedulePersist(selectedColumns, frozenColumns, columnWidths, sortBy, sortDir);
    }
  }, [selectedColumns, frozenColumns, columnWidths, sortBy, sortDir, schedulePersist]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [defualtData, setDefualtData] = useState([]);
  const [serachVal, setSearchVal] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [historyList, SetHistoryList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredValues, setFilteredvalues] = useState({
    cat: "",
    status: [],
    task: [],
    specType: "",
    // yieldValue: 90,
    yieldValueFrom: 0,
    yieldValueTo: 100,
    maintenanceCheck: "",
    maintenanceCheckPackage: "",
    maintenanceCheckInterval: "",
    maintenanceCheckPackageInterval: "",
  });
  const [filterStatus, setFilterStatus] = useState(false);
  const { selectedFlightModel, refreshFlightModels } = useFlightModel();

  const applySortData = useCallback((data, activeSortBy, activeSortDir) => {
    if (!activeSortBy || !activeSortDir) return data;
    return [...data].sort((a, b) => {
      let valA = a[activeSortBy];
      let valB = b[activeSortBy];

      if (valA === undefined || valA === null) valA = "";
      if (valB === undefined || valB === null) valB = "";

      if (typeof valA === "number" && typeof valB === "number") {
        return activeSortDir === "asc" ? valA - valB : valB - valA;
      }

      const strA = String(valA);
      const strB = String(valB);
      return activeSortDir === "asc"
        ? strA.localeCompare(strB, undefined, { numeric: true })
        : strB.localeCompare(strA, undefined, { numeric: true });
    });
  }, []);

  const deferredQuery = useDeferredValue(serachVal);

  const getHistoryMonths = useCallback(async () => {
    setSpinning(true);
       let flightModelToUse = selectedFlightModel;

    // fallback from context
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");setSpinning(false);
      setSpinning(false);
      return;
    }
    
    const queryParams = {
      params: {
        flight_model: flightModelToUse
        // localStorage.getItem("flight_model"),
      },
    };
    await historyAPI
      .getHistoryMonths(queryParams)
      .then((res) => {
        if (res.data.status === true) {
          const monthsArray = res.data.snapshots.map((ele) => {
            return {
              ...ele,
              value: ele.upper_range,
              label: `${formatInTimeZone(ele.upper_range, timeZone, "MMM dd yyyy hh:mm:ss a")}`,
            };
          });

          monthsArray.sort((a, b) => new Date(b.value) - new Date(a.value));
          SetHistoryList(monthsArray);

          const selectedMonthData = monthsArray.find(
            (month) => month.lower_range === null,
          );
          if (selectedMonthData) {
            setSelectedMonth(selectedMonthData.value);
          }
        } else {
          messageToast("error", res.data?.message);
        }
      })
      .catch((err) => {
        messageToast("error", "Internal Server Error");
      })
      .finally(() => {
        setSpinning(false);
      });
  }, [timeZone, messageToast]);

  const getHistoryData = useCallback(
    async (pageNo, pageCount, sortOverrides = {}) => {
      setSpinning(true);
      const selectedMonthData = historyList.find(
        (month) => month.value === selectedMonth,
      );
      let flightModelToUse = selectedFlightModel;

      // fallback from context
      if (!flightModelToUse) {
        const refreshed = await refreshFlightModels();
        flightModelToUse = refreshed?.selectedFlightModel || null;
      }

      if (!flightModelToUse) {
        messageToast("error", "No flight model selected.");
        setSpinning(false);
        return;
      }

      const activeSortBy = sortOverrides.sortBy ?? sortBy;
      const activeSortDir = sortOverrides.sortDir ?? sortDir;

      const bodyData = {
        flight_model: flightModelToUse,
        // localStorage.getItem("flight_model"),
        upload_history_id: selectedMonthData
          ? selectedMonthData.upload_history_id
          : "",
        upper_range: selectedMonthData ? selectedMonthData.upper_range : "",
        page: pageNo,
        page_size: pageCount,
        search: deferredQuery,
        sort_by: activeSortBy,
        sort_dir: activeSortDir,
      };
      await historyAPI
        .getHistoryList(bodyData)
        .then((res) => {
          if (res.data.status === true) {
            const sorted = applySortData(res.data.data || [], activeSortBy, activeSortDir);
            setHistoryData(sorted);
            setDefualtData(sorted);
            setTotalRecords(res.data.total);
            setPage(res.data.page);
            setPageSize(res.data.page_size);
          } else {
            // No snapshot/records for the current search or month: clear the grid
            // so the table shows its "No Records Found" empty state (matching
            // Rankings) instead of raising an error toast.
            setHistoryData([]);
            setDefualtData([]);
            setTotalRecords(0);
          }
        })
        .catch((err) => {
          messageToast("error", "Internal Server Error");
        })
        .finally(() => {
          setSpinning(false);
        });
    },
    [selectedMonth, historyList, messageToast, deferredQuery, sortBy, sortDir, applySortData, selectedFlightModel, refreshFlightModels],
  );

  const handleColumnSort = useCallback(
    (columnKey) => {
      if (!isSortableColumnKey(columnKey)) {
        return;
      }

      const nextSort = getNextSortState(sortBy, sortDir, columnKey);
      setSortBy(nextSort.sortBy);
      setSortDir(nextSort.sortDir);
      setPage(1);

      const sorted = applySortData(defualtData, nextSort.sortBy, nextSort.sortDir);
      setHistoryData(sorted);
    },
    [sortBy, sortDir, defualtData, applySortData],
  );

  useEffect(() => {
    let mounted = true;
    const initializeHistory = async () => {
      setSpinning(true);
      const preferencesLoaded = await loadGridPreferences();
      if (!mounted) return;
      await getHistoryMonths();
      if (!mounted) return;
      // Only enable write-back once the stored layout is known, so a failed load
      // cannot save the defaults over the user's real preferences.
      hasLoadedPreferencesRef.current = preferencesLoaded;
    };
    initializeHistory();
    return () => {
      mounted = false;
    };
  }, [getHistoryMonths, loadGridPreferences]);

  useEffect(() => {
    if (selectedMonth && selectedFlightModel) {
      getHistoryData(page, pageSize);
    }
  }, [selectedMonth, selectedFlightModel, page, pageSize, sortBy, sortDir, deferredQuery, getHistoryData]);

  const dateRangeLabel = useMemo(() => {
    const selectedMonthData = historyList.find(
      (month) => month.value === selectedMonth,
    );
    if (selectedMonthData) {
      const upperRange = selectedMonthData.upper_range;
      const lowerRange = selectedMonthData.lower_range;
      const formattedUpperRange = formatInTimeZone(
        upperRange,
        timeZone,
        "MMM dd yyyy hh:mm:ss a",
      );
      if (lowerRange) {
        const formattedLowerRange = formatInTimeZone(
          lowerRange,
          timeZone,
          "MMM dd yyyy hh:mm:ss a",
        );
        return `${formattedLowerRange} - ${formattedUpperRange}`;
      } else {
        return formattedUpperRange;
      }
    }
    return "";
  }, [selectedMonth, historyList, timeZone]);

  async function exportExcel() {
    setSpinning(true);
    try {
      const selectedMonthData = historyList.find(
        (month) => month.value === selectedMonth,
      );
        let flightModelToUse = selectedFlightModel;

    // fallback from context
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");setSpinning(false);
      setSpinning(false);
      return;
    }
      const bodyData = {
        flight_model: flightModelToUse,
        // localStorage.getItem("flight_model"),
        upload_history_id: selectedMonthData
          ? selectedMonthData.upload_history_id
          : "",
        upper_range: selectedMonthData ? selectedMonthData.upper_range : "",
        page: page,
        page_size: pageSize,
        search: serachVal,
        export: true,
      };

      const response = await historyAPI.exportRankList(bodyData);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Extract filename from content-disposition header if provided by backend
      const contentDisposition = response.headers["content-disposition"];
      let fileName = "History_export.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=(['"]?)([^'"\n;]*)\1/,
        );
        if (match?.[2]) fileName = match[2].trim();
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      messageToast("error", "Export failed. Please try again.");
    } finally {
      setSpinning(false);
    }
  }

  return (
    <div className="container_div">
      <Helmet>
        <title>SpecOptimizer | History</title>
      </Helmet>
      <HeaderTab currrentTab="history" />

      <Spin spinning={spinning}>
        <div className="box_wraper_div">
          <div className="table_head_wraper">
            <Text className="table_head">
              History of
              <label style={{ textTransform: "capitalize", marginLeft: "5px" }}>
                {selectedMonth && dateRangeLabel}
              </label>
            </Text>
            <div className="table_custom_buttons">
              <div
                style={{
                  borderRadius: "25px",
                  backgroundColor: "#EBEEF8",
                  color: "#737373",
                  display: "flex",
                  flex: "1 1 0px ",
                }}
              >
                <input
                  value={serachVal}
                  onChange={(e) => setSearchVal(e.target.value)}
                  className="searc_input"
                />
                <SearchOutlined
                  className="search_btn"
                  style={{
                    color: !serachVal ? "#737373" : "#ff4d4f",
                    fontSize: "18px",
                    padding: "10px",
                    borderRadius: "6px",
                  }}
                />
              </div>
              <Select
                style={{
                  fontSize: "13px",
                  width: 220,
                  flex: "1 1 0px ",
                }}
                placeholder="--slect month--"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e);
                  setPage(1);
                }}
                options={historyList}
              />
              <Dropdown
                placement="bottomRight"
                menu={{
                  items: [
                    {
                      key: "1",
                      label: (
                        <div
                          onClick={() => setOpenSelectColumns(true)}
                          style={{ fontSize: "18px", minWidth: "150px" }}
                        >
                          <GroupOutlined />{" "}
                          <Text style={{ marginLeft: "10px" }}>Columns</Text>
                        </div>
                      ),
                    },
                    {
                      key: "4",
                      label: (
                        <div
                          onClick={() => setOpenFreezeColumns(true)}
                          style={{ fontSize: "18px", minWidth: "150px" }}
                        >
                          <PushpinOutlined />{" "}
                          <Text style={{ marginLeft: "10px" }}>Freezing</Text>
                        </div>
                      ),
                    },
                    {
                      key: "3",
                      label: (
                        <div
                          onClick={exportExcel}
                          style={{ fontSize: "18px", minWidth: "150px" }}
                        >
                          <ExportOutlined />{" "}
                          <Text style={{ marginLeft: "10px" }}>
                            Export to Excel
                          </Text>
                        </div>
                      ),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <Badge dot={filterStatus}>
                  <Button
                    // disabled={defualtData.length > 0 ? false : true}
                    type="primary"
                    style={{ backgroundColor: "#EBEEF8", color: "#304CB2" }}
                    icon={<EllipsisOutlined />}
                  >
                    Actions
                  </Button>
                </Badge>
              </Dropdown>
            </div>
          </div>

           <HistoryTable
            listData={HistoryData}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            frozenColumns={frozenColumns}
            sortBy={sortBy}
            sortDir={sortDir}
            onColumnSort={handleColumnSort}
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            getHistoryDataFun={getHistoryData}
            selectedMonth={selectedMonth}
            filteredValues={filteredValues}
            serachVal={serachVal}
            setPage={setPage}
            setPageSize={setPageSize}
            setTotalRecords={setTotalRecords}
            setHistoryData={setHistoryData}
            pageType="History"
            columnWidths={columnWidths}
            setColumnWidths={setColumnWidths}
            onPreferenceAction={markPreferenceAction}
          />

          <HistorySelectedColumns
            open={openselectColumns}
            setOpen={setOpenSelectColumns}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            frozenColumns={frozenColumns}
            getRankingDataFun={getHistoryData}
            defualtColumns={DEFUALT_COLUMNS}
            selectedMonth={selectedMonth}
            messageToast={messageToast}
          />
          <HistoryFreezeColumns
            open={openFreezeColumns}
            setOpen={setOpenFreezeColumns}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            frozenColumns={frozenColumns}
            setFrozenColumns={setFrozenColumns}
            messageToast={messageToast}
            onPreferenceAction={markPreferenceAction}
          />
          <HistoryFilter
            open={openHistoryFilter}
            setOpen={setOpenHistoryFilter}
            listData={HistoryData}
            setListData={setHistoryData}
            defualtData={defualtData}
            filteredValues={filteredValues}
            setFilteredvalues={setFilteredvalues}
            serachVal={serachVal}
            setFilterStatus={setFilterStatus}
            selectedMonth={selectedMonth}
            page="History"
            pageSize={pageSize}
            pageNo={page}
          />
        </div>
      </Spin>

      <BottomTab />
      {contextHolder}
    </div>
  );
};
